/*
 * The only module in the app that names `window.gtag`. See ADR 0026 §4.
 *
 * The guard tests `window.__analyticsReady`, a flag public/index.html sets
 * *inside* its canonical-host gate, rather than `typeof window.gtag`. Those are
 * the same answer today and stop being the same the moment anything defines
 * gtag outside that gate - a Consent Mode default, say, which ADR 0025 §2 would
 * have added before it went on hold. Testing for the flag asks the question
 * that actually matters: was GA configured, or is this a dev session? Getting
 * it wrong reintroduces the localhost data leak PR #57 was opened to fix, and
 * it would be found the same slow way.
 *
 * Nothing here throws. An analytics helper that can break the download button
 * is worse than no analytics at all, so the send is wrapped and a failure is
 * swallowed - there is nowhere useful to report it to, by definition.
 */

type EventParams = Record<string, string | number | boolean>;

interface AnalyticsWindow extends Window {
    __analyticsReady?: boolean;
    gtag?: (...args: unknown[]) => void;
}

/** Send one GA4 event, or do nothing at all where analytics is not configured. */
export function track(event: string, params?: EventParams): void {
    if (typeof window === 'undefined') {
        return;
    }

    const w = window as AnalyticsWindow;
    if (!w.__analyticsReady || typeof w.gtag !== 'function') {
        return;
    }

    try {
        w.gtag('event', event, params ?? {});
    } catch {
        // Deliberately silent; see the header.
    }
}

export default track;
